import React, {
  isValidElement,
  useCallback,
  useEffect,
  useRef,
  Children,
  FunctionComponent,
  ReactNode,
  useState,
  Fragment,
  useMemo,
} from 'react';
import { Locale, useTranslation } from '@rbx/intl';
import ElementsShowInViewProvider from '@modules/ui/shared/components/ElementsShowInViewProvider';
import Router from 'next/router';
import { CreatorHubLayout } from '@rbx/creator-hub-navigation';
import HeadingItem from '@modules/utilities/interfaces/HeadingItem';
import { ArticleSchemaMarkupContext } from '@modules/seo/structured-data/types';
import MultiProvider from '@modules/miscellaneous/common/components/MultiProvider';
import AppMainConfigProvider from '@modules/ui/shared/components/AppMainConfigProvider';
import { TestId } from '@modules/utilities/constants/testConstants';
import { Grid } from '@rbx/ui';
import MinimumSpecBanner from '@modules/layout/topNavigation/components/MinimumSpecBanner';
import useOpenStudio from '@modules/useStudio/hooks/useOpenStudio';
import { EStudioTaskType } from '@rbx/studio';
import useNavigationSearch from '@modules/layout/topNavigation/hooks/useNavigationSearch';
import SearchContainer from '@modules/ui/search/SearchContainer';
import { ESearchInteraction } from '@modules/eventStream/enum/DocsSiteSearch';
import ReferenceHeading from '@modules/apiReference/page/sharedUI/components/ReferenceHeading';
import throwError from '@modules/utilities/error';
import AppMainNavbar from './AppMainNavbar';
import AppHeading from './AppHeading';
import AppRightNav from './AppRightNav';
import AppMainStateProvider from './AppMainStateProvider';
import AppContent from './AppContent';
import AppLeftNav from './AppLeftNav';
import useAppLayoutStyles from './AppLayout.styles';
import { useAppConfig } from './AppConfigProvider';
import useKeyboardNavigation from '../hooks/useKeyboardNavigation';
import { onKeyDown } from '../utils/keyboardNavigationHandler';
import ModerationWrapper from './ModerationWrapper';
import AppHead from './AppHead';
import AppLayoutMain from './AppLayoutMain';
import AppIAHeading from './AppIAHeading';
import AppPageContent from './AppPageContent';

export type TOpenGraphMeta = {
  locale: Locale;
  title: string;
  description: string;
  imageUrl: string;
  baseUrl: string;
};

export enum LayoutType {
  Landing,
  Default,
  ApiReference,
  OpenCloudV1,
  OpenCloudV2,
}

interface LayoutSlots {
  mainNavbar?: JSX.Element;
  heading?: JSX.Element;
  rightNav?: JSX.Element;
  mainContent?: JSX.Element;
  sideBar?: JSX.Element;
  iaHeading?: JSX.Element;
}

// TODO (@ahua, CRK-3500): layout of react components should match layout of DOM
const generateLayoutSlots = (children: ReactNode): LayoutSlots =>
  Children.toArray(children).reduce<LayoutSlots>((slot, child) => {
    if (!isValidElement(child)) {
      return slot;
    }

    switch (child.type) {
      case AppMainNavbar:
        if (typeof slot.mainNavbar !== 'undefined') {
          throw new Error(
            `Duplicate ${AppMainNavbar.displayName ?? AppMainNavbar.name} found under AppLayout!`,
          );
        }
        return { ...slot, mainNavbar: child };
      case AppHeading:
        if (typeof slot.heading !== 'undefined') {
          throw new Error(
            `Duplicate ${AppHeading.displayName ?? AppHeading.name} found under AppLayout!`,
          );
        }
        return { ...slot, heading: child };
      case ReferenceHeading:
        if (typeof slot.heading !== 'undefined') {
          throwError(
            `AppLayout: Duplicate ${ReferenceHeading.displayName ?? ReferenceHeading.name}`,
          );
        }
        return { ...slot, heading: child };
      case AppRightNav:
        if (typeof slot.rightNav !== 'undefined') {
          throw new Error(
            `Duplicate ${AppRightNav.displayName ?? AppRightNav.name} found under AppLayout!`,
          );
        }
        return { ...slot, rightNav: child };
      case AppContent:
        if (typeof slot.mainContent !== 'undefined') {
          throw new Error(
            `Duplicate ${AppContent.displayName ?? AppContent.name} found under AppLayout!`,
          );
        }
        return { ...slot, mainContent: child };
      case AppLeftNav:
        if (typeof slot.sideBar !== 'undefined') {
          throw new Error(
            `Duplicate ${AppLeftNav.displayName ?? AppLeftNav.name} found under AppLayout!`,
          );
        }
        return { ...slot, sideBar: child };
      case AppIAHeading:
        if (typeof slot.iaHeading !== 'undefined') {
          throw new Error(
            `Duplicate ${AppIAHeading.displayName ?? AppIAHeading.name} found under AppLayout!`,
          );
        }
        return { ...slot, iaHeading: child };
      default:
        return slot;
    }
  }, {});

/* eslint-disable react/no-unused-prop-types */
export interface AppLayoutProps {
  siteTitle: string;
  pageTitleSuffixKey?: string;
  pageTitle?: string;
  pageMetaDescription?: string | null;
  type?: LayoutType;
  headings?: HeadingItem[];
  openGraphMeta: Partial<TOpenGraphMeta>;
  seoStructureData?: ArticleSchemaMarkupContext;
  slug?: string;
  showHrefLang?: boolean;
  testid: string;
  renderBanner?: (className?: string) => JSX.Element | undefined;
}
/* eslint-enable react/no-unused-prop-types */

const AppLayout: FunctionComponent<React.PropsWithChildren<AppLayoutProps>> = ({
  children,
  headings,
  type = LayoutType.Default,
  testid,
  pageTitle,
  siteTitle,
  renderBanner,
}) => {
  const [observer, setObserver] = useState<HTMLElement | null>();
  const { mainNavbar, heading, rightNav, mainContent, sideBar } = generateLayoutSlots(children);
  const layoutContainerRef = useRef<HTMLDivElement>(null);
  // reset scroll position if route change happened
  const handleRouteChange = useCallback(() => {
    layoutContainerRef.current?.scrollTo(0, 0);
  }, []);
  const { showSecondaryNavigation } = useAppConfig();
  useKeyboardNavigation(onKeyDown);

  const [showMinSpecBanner, setShowMinSpecBanner] = useState(false);
  const {
    classes: { root, leftNav },
  } = useAppLayoutStyles({ showSecondaryNavigation, showMinSpecBanner });

  useEffect(() => {
    Router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      Router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [handleRouteChange]);

  useEffect(() => {
    setObserver(layoutContainerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutContainerRef.current]);

  return (
    <Grid className={root} container wrap='nowrap' justifyItems='center'>
      <Grid component='aside' XSmall='auto' data-testid={TestId.Sidebar} className={leftNav} item>
        {sideBar}
      </Grid>
      <MultiProvider
        providers={[
          /* eslint-disable react/jsx-key */
          <ElementsShowInViewProvider observer={observer} />,
          <AppMainStateProvider />,
          <AppMainConfigProvider
            pageTitle={pageTitle ?? siteTitle}
            headings={headings}
            layoutType={type}
          />,
          /* eslint-disable react/jsx-key */
        ]}>
        <ModerationWrapper>
          <AppLayoutMain
            ref={layoutContainerRef}
            mainNavbar={mainNavbar}
            heading={heading}
            renderBanner={renderBanner}
            rightNav={rightNav}
            mainContent={mainContent}
            testid={testid}
            onBannerToggle={setShowMinSpecBanner}
          />
        </ModerationWrapper>
      </MultiProvider>
    </Grid>
  );
};

type IALayoutProps = Pick<
  AppLayoutProps,
  'headings' | 'type' | 'renderBanner' | 'pageTitle' | 'siteTitle'
>;
const IALayout: FunctionComponent<React.PropsWithChildren<IALayoutProps>> = ({
  pageTitle,
  siteTitle,
  type = LayoutType.Default,
  headings,
  renderBanner,
  children,
}) => {
  const layoutContainerRef = useRef<HTMLDivElement>(null);
  const [observer, setObserver] = useState<HTMLElement | null>();
  const { iaHeading, heading, rightNav, mainContent, sideBar } = generateLayoutSlots(children);
  const [showMinSpecBanner, setShowMinSpecBanner] = useState(false);
  const { open, dialog } = useOpenStudio();
  const { setSearchDialogOpenWithEvent } = useNavigationSearch();

  const handleRouteChange = useCallback(() => {
    layoutContainerRef.current?.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    Router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      Router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [handleRouteChange]);

  useEffect(() => {
    setObserver(layoutContainerRef.current);
  }, [layoutContainerRef]);

  const openStudio = useCallback(() => {
    open({ task: EStudioTaskType.Default });
  }, [open]);

  return (
    <MultiProvider
      providers={[
        /* eslint-disable react/jsx-key */
        <ElementsShowInViewProvider observer={observer} />,
        <AppMainStateProvider />,
        <AppMainConfigProvider
          pageTitle={pageTitle ?? siteTitle}
          headings={headings}
          layoutType={type}
        />,
        /* eslint-disable react/jsx-key */
      ]}>
      <CreatorHubLayout>
        <CreatorHubLayout.Rail openStudio={openStudio} secondarySize='medium'>
          {sideBar}
        </CreatorHubLayout.Rail>
        <CreatorHubLayout.Header
          onSearchClick={() =>
            setSearchDialogOpenWithEvent({
              searchDialogOpen: true,
              interaction: ESearchInteraction.NavSearchIcon,
            })
          }>
          <SearchContainer />
          {iaHeading}
        </CreatorHubLayout.Header>
        <CreatorHubLayout.PageContent
          ref={layoutContainerRef}
          banner={renderBanner && renderBanner()}>
          <AppPageContent rightNav={rightNav} layoutType={type}>
            {dialog}
            {showMinSpecBanner && <MinimumSpecBanner onBannerToggle={setShowMinSpecBanner} />}
            {heading}
            {mainContent}
          </AppPageContent>
          <ModerationWrapper />
        </CreatorHubLayout.PageContent>
      </CreatorHubLayout>
    </MultiProvider>
  );
};

export interface AppLayoutWrapperProps {
  forcedSiteTitle?: string;
  pageTitleSuffixKey?: string;
  pageTitle?: string;
  pageMetaDescription?: string | null;
  type?: LayoutType;
  headings?: HeadingItem[];
  openGraphMeta: Partial<TOpenGraphMeta>;
  seoStructureData?: ArticleSchemaMarkupContext;
  slug?: string;
  showHrefLang?: boolean;
  testid: string;
  renderBanner?: (className?: string) => JSX.Element | undefined;
}

const AppLayoutWrapper: FunctionComponent<React.PropsWithChildren<AppLayoutWrapperProps>> = ({
  children,
  forcedSiteTitle,
  pageTitleSuffixKey = 'Label.DocumentationCreatorHub',
  pageTitle,
  ...props
}) => {
  const { enableNewLayout } = useAppConfig();
  const { translate } = useTranslation();

  const siteTitle = useMemo(() => {
    if (forcedSiteTitle) return forcedSiteTitle;
    return pageTitle
      ? translate('Heading.DocumentationSiteTitle', {
          pageTitle,
          SiteName: translate(pageTitleSuffixKey),
        })
      : translate(pageTitleSuffixKey);
  }, [pageTitle, pageTitleSuffixKey, translate, forcedSiteTitle]);

  if (enableNewLayout) {
    return (
      <Fragment>
        <AppHead siteTitle={siteTitle} {...props} />
        <IALayout siteTitle={siteTitle} {...props}>
          {children}
        </IALayout>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <AppHead siteTitle={siteTitle} {...props} />
      <AppLayout siteTitle={siteTitle} {...props}>
        {children}
      </AppLayout>
    </Fragment>
  );
};

export default AppLayoutWrapper;
